import { faMemo } from '@fortawesome/pro-regular-svg-icons';
import {
  faThumbtack,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage, FormattedNumber, defineMessages } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { InGameCurrency, Rarity, Sport } from '__generated__/globalTypes';
import { Button } from 'atoms/buttons/Button';
import { IconButton } from 'atoms/buttons/IconButton';
import { FontAwesomeIcon } from 'atoms/icons';
import { Boost } from 'atoms/icons/Boost';
import { Energy } from 'atoms/icons/Energy';
import { GemIcon } from 'atoms/icons/GemIcon';
import { Shards } from 'atoms/icons/Shards';
import { Centered } from 'atoms/layout/Centered';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { Tooltip } from 'atoms/tooltip/Tooltip';
import { BrandL, BrandS, LabelS } from 'atoms/typography';
import { DialogKey } from 'components/navigation/WithDialogs';
import {
  ANY_SPORT_ESSENCE,
  ANY_SPORT_ESSENCE_TYPE,
  MLB_MARKET_COMMON_MARKET,
  MY_SORARE_TRANSACTIONS_ESSENCE,
} from 'constants/__generated__/routes';
import { ANY_SPORT_MARKET_COMMON_PACKS } from 'constants/routes';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useSportContext } from 'contexts/sport';
import { useIsLaptop } from 'hooks/device/useIsLaptop';
import { useGetMyCardsBoostLink } from 'hooks/my-cards/useGetMyCardsBoostLink';
import { useOpenDialog } from 'hooks/navigation/useOpenDialog';
import { LIFECYCLE, useLifecycle } from 'hooks/useLifecycle';
import { formatScarcity } from 'lib/cards';
import { getRarityColor } from 'lib/colors';
import { glossary } from 'lib/glossary';
import { inGameCurrencyToRarity } from 'lib/scarcity';
import { laptopAndAbove } from 'style/mediaQuery';
import type { Color } from 'style/types';

const CenteredTop = styled(Centered)`
  align-items: start;
`;

export enum InGameCurrencyAgreggated {
  ENERGY = 'ENERGY',
  XP = 'XP',
  SHARDS = 'SHARDS',
  BLUEPRINTS = 'BLUEPRINT',
  GEMS = 'GEMS',
}

type DetailsProps = { onClick?: () => void };

export type CurrencyData = {
  id: InGameCurrencyAgreggated;
  icon: ReactNode;
  amount?: number;
  details?: (props: DetailsProps) => ReactNode;
  visibleOnSports: Sport[];
  expanded?: boolean;
  onClick?: () => void;
};

const SelectedCurrencyWrapper = styled(Vertical).attrs({
  gap: 2,
})`
  position: relative;
  padding: var(--double-unit) var(--unit) var(--unit) var(--unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--intermediate-unit);
`;
const RaritiesWrapper = styled(Horizontal)`
  align-items: stretch;
  @media ${laptopAndAbove} {
    flex-direction: column;
    gap: var(--half-unit);
  }
`;
const RarityWrapper = styled(Vertical)`
  justify-content: space-between;
  align-items: center;
  flex: 1;
  text-align: center;
  border-radius: var(--unit);
  padding: var(--unit);
  background-color: var(--c-nd-50);

  @media ${laptopAndAbove} {
    flex-direction: row;
    width: 100%;
    padding: var(--half-unit) var(--unit);

    background-color: transparent;
    &:hover {
      background-color: var(--c-nd-50);
    }
  }
`;
const RarityContainer = styled(Vertical).attrs({ center: true })`
  width: 100%;
  align-items: stretch;
  @media ${laptopAndAbove} {
    flex-direction: row;
  }
`;
const StyledPinButton = styled(IconButton)`
  transform: rotate(45deg);
  align-self: center;
  @media ${laptopAndAbove} {
    align-self: flex-start;
  }
`;

const messages = defineMessages({
  capAlmostReached: {
    id: 'useUserCurrencies.capAlmostReached',
    defaultMessage: 'Cap almost reached',
  },
});

const MIX_SHARD_COLOR: Color = 'var(--c-green-400)';
const MIX_XP_COLOR: Color = 'var(--c-rewards-blue)';

export const useUserCurrencies = (
  expandedCurrencies?: InGameCurrencyAgreggated[]
): CurrencyData[] => {
  const navigate = useNavigate();

  const { sport, generateSportContextPath } = useSportContext();
  const getMyCardsBoostLink = useGetMyCardsBoostLink();
  const { currentUser } = useCurrentUserContext();
  const [seeAllEnergy, setSeeAllEnergy] = useState(false);
  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const isLaptop = useIsLaptop();

  const { energyBalance, xpBalances, cardShardsChests } = currentUser || {};

  const filteredXpBalances = xpBalances?.filter(balance =>
    lifecycle?.hideCommonFeatures
      ? balance.currency !== InGameCurrency.COMMON_XP
      : true
  );
  const totalEnergyBalance = energyBalance?.reduce(
    (sum, { currency, amount }) => {
      const rarity = inGameCurrencyToRarity[currency];
      if (!rarity) return sum;
      // do not show energy balance if user has no card of this rarity
      if (!seeAllEnergy && !currentUser?.cardCounts?.[rarity]) {
        return sum;
      }
      return sum + amount;
    },
    0
  );

  const { pinnedShardRarity, pinnedXpRarity } = lifecycle || {};

  const shardsAmount = pinnedShardRarity
    ? cardShardsChests?.find(({ rarity }) => rarity === pinnedShardRarity)
        ?.cardShardsCount || 0
    : cardShardsChests
        ?.filter(
          ({ rarity }) =>
            !lifecycle?.hideCommonFeatures || rarity !== Rarity.common
        )
        .reduce((sum, { cardShardsCount }) => sum + cardShardsCount, 0);

  const firstNonZeroShardsBalance = (cardShardsChests || []).find(
    ({ cardShardsCount }) => cardShardsCount > 0
  );
  const firstNonZeroShardsBalanceRarity = firstNonZeroShardsBalance?.rarity;

  const xpAmount = pinnedXpRarity
    ? filteredXpBalances?.find(
        ({ currency }) => inGameCurrencyToRarity[currency] === pinnedXpRarity
      )?.amount || 0
    : filteredXpBalances?.reduce((sum, { amount }) => sum + amount, 0);

  const firstNonZeroXPBalance = (filteredXpBalances || []).find(
    ({ amount }) => amount > 0
  );
  const firstNonZeroXPBalanceRarity = firstNonZeroXPBalance?.currency
    ? inGameCurrencyToRarity[firstNonZeroXPBalance.currency]
    : undefined;

  const gemsCount = currentUser?.gemsBalances[0]?.amount || 0;

  const pinCurrencyRarity = (currencyType: 'shard' | 'xp', rarity: Rarity) => {
    if (!lifecycle) {
      return;
    }

    const lifecycleKey =
      currencyType === 'shard'
        ? LIFECYCLE.pinnedShardRarity
        : LIFECYCLE.pinnedXpRarity;

    updateLifecycle(
      lifecycleKey,
      lifecycle?.[lifecycleKey] === rarity ? false : rarity
    );
  };

  const renderPinButton = (
    currencyType: 'shard' | 'xp',
    rarity: Rarity,
    pinnedRarity: Rarity | undefined | false
  ) => (
    <StyledPinButton
      size="compact"
      onClick={() => pinCurrencyRarity(currencyType, rarity)}
      color={pinnedRarity === rarity ? 'secondary' : 'quaternary'}
    >
      <FontAwesomeIcon icon={faThumbtack} size="xs" />
    </StyledPinButton>
  );

  const openDialog = useOpenDialog();

  return [
    {
      id: InGameCurrencyAgreggated.SHARDS,
      icon: (
        <Shards
          size={2}
          color={
            pinnedShardRarity
              ? getRarityColor(pinnedShardRarity)
              : MIX_SHARD_COLOR
          }
        />
      ),
      amount: shardsAmount,
      details: ({ onClick }: DetailsProps) => (
        <SelectedCurrencyWrapper>
          <CenteredTop
            right={
              <IconButton
                size="small"
                color="quaternary"
                to={MY_SORARE_TRANSACTIONS_ESSENCE}
                icon={faMemo}
              />
            }
          >
            <Vertical gap={0.5} center>
              <Shards
                size={2}
                color={
                  pinnedShardRarity
                    ? getRarityColor(pinnedShardRarity)
                    : MIX_SHARD_COLOR
                }
              />
              <BrandS
                color={
                  pinnedShardRarity
                    ? getRarityColor(pinnedShardRarity)
                    : MIX_SHARD_COLOR
                }
              >
                <FormattedMessage
                  id="useUserCurrencies.essence"
                  defaultMessage="Essence"
                />
              </BrandS>
              <BrandL>
                <FormattedNumber value={shardsAmount || 0} />
              </BrandL>
            </Vertical>
          </CenteredTop>

          <RaritiesWrapper>
            {cardShardsChests
              ?.filter(shard =>
                lifecycle?.hideCommonFeatures
                  ? shard.rarity !== Rarity.common
                  : // TODO: handle winter shards
                    !shard.cardSet?.slug || shard.cardSet.slug !== 'winter'
              )
              .map(({ cardShardsCount, rarity }) => {
                return (
                  <RarityContainer key={rarity}>
                    {!!isLaptop &&
                      renderPinButton('shard', rarity, pinnedShardRarity)}
                    <RarityWrapper
                      as={Link}
                      to={generateSportContextPath(ANY_SPORT_ESSENCE_TYPE, {
                        params: { type: rarity },
                      })}
                      onClick={onClick}
                    >
                      <LabelS>{formatScarcity(rarity, null, !isLaptop)}</LabelS>
                      <BrandS>
                        <FormattedNumber value={cardShardsCount} />
                      </BrandS>
                    </RarityWrapper>
                    {!isLaptop &&
                      renderPinButton('shard', rarity, pinnedShardRarity)}
                  </RarityContainer>
                );
              })}
          </RaritiesWrapper>
        </SelectedCurrencyWrapper>
      ),
      visibleOnSports: [Sport.FOOTBALL, Sport.NBA, Sport.BASEBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.SHARDS),
      onClick: () => {
        const rarity =
          lifecycle?.pinnedShardRarity || firstNonZeroShardsBalanceRarity;
        if (rarity === Rarity.common) {
          navigate(
            sport === Sport.BASEBALL
              ? MLB_MARKET_COMMON_MARKET
              : generateSportContextPath(ANY_SPORT_MARKET_COMMON_PACKS)
          );
        } else if (rarity) {
          navigate(
            generateSportContextPath(ANY_SPORT_ESSENCE_TYPE, {
              params: { type: rarity },
            })
          );
        } else {
          navigate(generateSportContextPath(ANY_SPORT_ESSENCE));
        }
      },
    },
    {
      id: InGameCurrencyAgreggated.XP,
      icon: (
        <Boost
          size={2}
          color={pinnedXpRarity ? getRarityColor(pinnedXpRarity) : MIX_XP_COLOR}
        />
      ),
      amount: xpAmount,
      details: ({ onClick }: DetailsProps) => (
        <SelectedCurrencyWrapper>
          <Vertical gap={0.5} center>
            <Boost
              size={2}
              color={
                pinnedXpRarity ? getRarityColor(pinnedXpRarity) : MIX_XP_COLOR
              }
            />
            <BrandS
              color={
                pinnedXpRarity ? getRarityColor(pinnedXpRarity) : MIX_XP_COLOR
              }
            >
              <FormattedMessage
                id="XpBalances.xpReserve"
                defaultMessage="XP Reserve"
              />
            </BrandS>
            <BrandL>
              <FormattedNumber value={xpAmount || 0} />
            </BrandL>
          </Vertical>
          <RaritiesWrapper>
            {filteredXpBalances?.map(
              ({ currency, amount, capAlmostReached }) => {
                const rarity = inGameCurrencyToRarity[currency];
                if (!rarity) return null;
                return (
                  <RarityContainer key={currency}>
                    {!!isLaptop &&
                      renderPinButton('xp', rarity, pinnedXpRarity)}
                    <RarityWrapper
                      as={Link}
                      to={getMyCardsBoostLink({
                        rarity,
                        sport,
                      })}
                      onClick={onClick}
                    >
                      <LabelS>{formatScarcity(rarity)}</LabelS>
                      <Horizontal>
                        <BrandS>
                          <FormattedNumber value={amount} />
                        </BrandS>
                        {!!capAlmostReached && (
                          <Tooltip
                            title={
                              <FormattedMessage
                                {...messages.capAlmostReached}
                              />
                            }
                          >
                            <FontAwesomeIcon
                              size="xs"
                              color="var(--c-yellow-400)"
                              icon={faTriangleExclamation}
                            />
                          </Tooltip>
                        )}
                      </Horizontal>
                    </RarityWrapper>
                    {!isLaptop && renderPinButton('xp', rarity, pinnedXpRarity)}
                  </RarityContainer>
                );
              }
            )}
          </RaritiesWrapper>
        </SelectedCurrencyWrapper>
      ),
      visibleOnSports: [Sport.FOOTBALL, Sport.NBA, Sport.BASEBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.XP),
      onClick: () => {
        const rarity = lifecycle?.pinnedXpRarity || firstNonZeroXPBalanceRarity;
        if (rarity) {
          navigate(getMyCardsBoostLink({ rarity, sport }));
        } else {
          navigate(
            sport === Sport.BASEBALL
              ? MLB_MARKET_COMMON_MARKET
              : generateSportContextPath(ANY_SPORT_MARKET_COMMON_PACKS)
          );
        }
      },
    },
    {
      id: InGameCurrencyAgreggated.ENERGY,
      icon: <Energy size={2} color="var(--c-yellow-300)" />,
      amount: totalEnergyBalance,
      details: () => (
        <SelectedCurrencyWrapper>
          <Vertical gap={0.5} center>
            <Energy size={2} color="var(--c-yellow-300)" />
            <BrandS color="var(--c-yellow-300)">
              <FormattedMessage
                id="useUserCurrencies.energy"
                defaultMessage="Energy"
              />
            </BrandS>
            <BrandL>
              <FormattedNumber value={totalEnergyBalance || 0} />
            </BrandL>
          </Vertical>
          <RaritiesWrapper gap={0}>
            {energyBalance?.map(({ currency, amount, capAlmostReached }) => {
              const rarity = inGameCurrencyToRarity[currency];
              if (!rarity) return null;
              // do not show energy balance if user has no card of this rarity
              if (!seeAllEnergy && !currentUser?.cardCounts?.[rarity]) {
                return null;
              }
              return (
                <RarityWrapper key={currency}>
                  <LabelS>{formatScarcity(rarity)}</LabelS>
                  <Horizontal>
                    <BrandS>
                      <FormattedNumber value={amount} />
                    </BrandS>
                    {!!capAlmostReached && (
                      <Tooltip
                        title={
                          <FormattedMessage {...messages.capAlmostReached} />
                        }
                      >
                        <FontAwesomeIcon
                          size="xs"
                          color="var(--c-yellow-400)"
                          icon={faTriangleExclamation}
                        />
                      </Tooltip>
                    )}
                  </Horizontal>
                </RarityWrapper>
              );
            })}
          </RaritiesWrapper>
          <Button
            size="compact"
            fullWidth
            color="quaternary"
            disableDebounce
            onClick={() => setSeeAllEnergy(prev => !prev)}
          >
            <FormattedMessage
              {...(seeAllEnergy ? glossary.showLess : glossary.showMore)}
            />
          </Button>
        </SelectedCurrencyWrapper>
      ),
      visibleOnSports: [Sport.BASEBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.ENERGY),
    },
    {
      id: InGameCurrencyAgreggated.GEMS,
      icon: <GemIcon size={2} />,
      amount: gemsCount,
      details: () => (
        <SelectedCurrencyWrapper>
          <Vertical gap={0.5} center>
            <GemIcon size={2} />
            <BrandS color="var(--c-green-400)">
              <FormattedMessage
                id="useUserCurrencies.gems"
                defaultMessage="Gems"
              />
            </BrandS>
            <BrandL>
              <FormattedNumber value={gemsCount || 0} />
            </BrandL>
          </Vertical>
          <Button
            size="medium"
            fullWidth
            color="quaternary"
            onClick={() => openDialog(DialogKey.buyGems, 'true')}
          >
            <FormattedMessage
              id="useUserCurrencies.getGems"
              defaultMessage="Get Gems"
            />
          </Button>
        </SelectedCurrencyWrapper>
      ),
      onClick: () => openDialog(DialogKey.buyGems, 'true'),
      visibleOnSports: [Sport.FOOTBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.GEMS),
    },
  ].filter(Boolean);
};
